import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 2fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function DataTypeVariableControlStructure() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    // Junior Dev Note: useEffect handles window scroll events to toggle the FAB button
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    // Constant data moved inside component or defined outside to prevent useMemo issues
    const primitiveDataTypes = useMemo(() => [
        { type: "byte", description: "Stores integer values from -128 to 127", example: "byte b = 100;" },
        { type: "short", description: "Stores integer values from -32,768 to 32,767", example: "short s = 5000;" },
        { type: "int", description: "Stores integers (-2^31 to 2^31-1)", example: "int age = 25;" },
        { type: "long", description: "Stores large integer values", example: "long distance = 123456789L;" },
        { type: "float", description: "Stores decimals (6–7 digits precision)", example: "float pi = 3.14f;" },
        { type: "double", description: "Stores decimals (15 digits precision)", example: "double price = 19.99;" },
        { type: "char", description: "Stores a single character", example: "char grade = 'A';" },
        { type: "boolean", description: "Stores true or false", example: "boolean isJavaFun = true;" },
    ], []);

    const nonPrimitiveDataTypes = useMemo(() => [
        { type: "String", description: "Stores text", example: `String name = "Hazzan";` },
        { type: "Array", description: "Stores multiple values", example: `int[] nums = {1,2,3};` },
        { type: "Class", description: "Blueprint for objects", example: `class Car { String model = "Tesla"; }` },
        { type: "Object", description: "Instance of a class", example: `Car myCar = new Car();` },
        { type: "Interface", description: "Defines a contract", example: `interface Animal { void sound(); }` },
        { type: "Enum", description: "Fixed set of constants", example: `enum Day { MON, TUE, WED };` },
    ], []);

    const controlStructures = useMemo(() => [
        {
            title: "if-else Decision Making",
            code: `public class Main {\n  public static void main(String[] args) {\n    int age = 20;\n    if (age >= 18) {\n      System.out.println("Status: Adult");\n    } else {\n      System.out.println("Status: Minor");\n    }\n  }\n}`,
            diagram: "Flowchart showing conditional branching logic."
        },
        {
            title: "for Loop (Definite Iteration)",
            code: `for(int i = 1; i <= 5; i++) {\n  System.out.println("Iteration Count: " + i);\n}`,
            diagram: "Visual representation of a loop incrementing a counter."
        },
        {
            title: "while Loop (Indefinite Iteration)",
            code: `int i = 1;\nwhile(i <= 5) {\n  System.out.println("Working... Iteration: " + i);\n  i++;\n}`,
            diagram: "Logic flow for a condition-checked entry loop."
        },
        {
            title: "switch-statement (Multi-way Branch)",
            code: `int day = 4;\nswitch (day) {\n  case 1: System.out.println("Monday"); break;\n  case 4: System.out.println("Thursday"); break;\n  default: System.out.println("Unknown Day"); break;\n}`,
            diagram: "Mapping multiple constant values to specific code blocks."
        },
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                       // backgroundColor: "goldenrod",
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    JAVA FUNDAMENTALS
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            1. Data Types & Variables
                        </Typography>

                        <Typography mb={3} sx={{ opacity: 0.9 }}>
                            Java is a <strong>statically-typed</strong> language. Understanding memory management is key to mastering development.
                        </Typography>



                        {/* ✅ Primitive Section */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Primitive Data Types</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Type</div>
                                    <div>Description</div>
                                    <div>Example</div>
                                </ResponsiveDataRow>
                            )}
                            {primitiveDataTypes.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod" }}>{item.type}</Box>
                                    <Box sx={{ fontSize: "0.95rem" }}>{item.description}</Box>
                                    <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.85rem" }}>{item.example}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>

                        {/* ✅ Non-Primitive Section */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 6, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Non-Primitive (Reference)</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Type</div>
                                    <div>Description</div>
                                    <div>Example</div>
                                </ResponsiveDataRow>
                            )}
                            {nonPrimitiveDataTypes.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod" }}>{item.type}</Box>
                                    <Box sx={{ fontSize: "0.95rem" }}>{item.description}</Box>
                                    <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.85rem" }}>{item.example}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Control Structure Sections with Copy & Try functionality */}
                {controlStructures.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>{item.diagram}</Typography>

                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/javadashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
